/**
 * DEMO 모드 테스트용 AuthContext 예시
 *
 * 개발 및 테스트 환경에서 RBAC 시스템을 테스트하기 위한
 * 예시 AuthContext 객체들입니다.
 *
 * **사용 방법:**
 * 1. 콘솔에서 직접 import하여 사용
 * 2. DEMO 모드에서 자동 로그인 시 사용
 * 3. 테스트 코드에서 사용
 *
 * **주의사항:**
 * - 실제 운영 환경에서는 사용하지 마세요
 * - Firestore에서 실제 데이터를 조회하여 AuthContext를 생성해야 합니다
 */

import { Timestamp } from 'firebase/firestore';
import { AuthContext } from '../types/rbac';
import { RoleAssignment } from '../types/roleAssignment';

/**
 * 일반 직원 (general) AuthContext
 *
 * 김일반 사원의 예시입니다.
 * - 역할: general (일반 직원)
 * - 소속: 개발팀 (org_team_dev)
 * - 권한: 본인 정보만 조회/수정 가능
 */
export const demoUserContext_general: AuthContext = {
  userId: 'user_general_001',
  roleAssignments: [
    {
      id: 'assign_general_001',
      userId: 'user_general_001',
      roleId: 'general',
      isActive: true,
      createdAt: Timestamp.now(),
      createdBy: 'admin',
      notes: '일반 직원 기본 역할',
    },
  ],
  orgUnitId: 'org_team_dev',
  isLoaded: true,
};

/**
 * TL (팀 리더) AuthContext
 *
 * 박TL의 예시입니다.
 * - 역할: tl (TL)
 * - 소속: 개발팀 (org_team_dev)
 * - 권한: 일반 직원 + MM 등록 권한
 */
export const demoUserContext_tl: AuthContext = {
  userId: 'user_tl_001',
  roleAssignments: [
    {
      id: 'assign_tl_001',
      userId: 'user_tl_001',
      roleId: 'tl',
      isActive: true,
      createdAt: Timestamp.now(),
      createdBy: 'admin',
      notes: 'TL 역할',
    },
  ],
  orgUnitId: 'org_team_dev',
  isLoaded: true,
};

/**
 * 조직장 (org_manager) AuthContext
 *
 * 최팀장의 예시입니다.
 * - 역할: org_manager (조직장 - 개발팀)
 * - 소속: 개발팀 (org_team_dev)
 * - 권한: 개발팀 및 하위 조직 관리, 팀원 근로 정보 조회, 승인 권한
 */
export const demoUserContext_orgManager: AuthContext = {
  userId: 'user_org_manager_001',
  roleAssignments: [
    {
      id: 'assign_org_manager_001',
      userId: 'user_org_manager_001',
      roleId: 'org_manager',
      orgUnitId: 'org_team_dev',
      isActive: true,
      createdAt: Timestamp.now(),
      createdBy: 'admin',
      notes: '개발팀 팀장',
    },
  ],
  orgUnitId: 'org_team_dev',
  isLoaded: true,
};

/**
 * 조직장 + 승인자 (다중 역할) AuthContext
 *
 * 이부장의 예시입니다.
 * - 역할: org_manager (조직장 - 기술본부) + approver (승인자 - 기술본부)
 * - 소속: 기술본부 (org_dept_tech)
 * - 권한: 기술본부 및 하위 조직 관리, 승인 처리
 *
 * **다중 역할 예시:**
 * 한 사용자가 여러 역할을 동시에 가질 수 있습니다.
 * 이 경우 모든 역할의 권한을 합쳐서 사용합니다.
 */
export const demoUserContext_orgManagerAndApprover: AuthContext = {
  userId: 'user_dept_manager_001',
  roleAssignments: [
    {
      id: 'assign_org_manager_002',
      userId: 'user_dept_manager_001',
      roleId: 'org_manager',
      orgUnitId: 'org_dept_tech',
      isActive: true,
      createdAt: Timestamp.now(),
      createdBy: 'admin',
      notes: '기술본부 조직장',
    },
    {
      id: 'assign_approver_001',
      userId: 'user_dept_manager_001',
      roleId: 'approver',
      orgUnitId: 'org_dept_tech',
      isActive: true,
      createdAt: Timestamp.now(),
      createdBy: 'admin',
      notes: '기술본부 승인자',
    },
  ],
  orgUnitId: 'org_dept_tech',
  isLoaded: true,
};

/**
 * 인사관리자 (role_hr) AuthContext
 *
 * 정인사의 예시입니다.
 * - 역할: role_hr (인사관리자)
 * - 소속: 인사팀 (org_team_hr)
 * - 권한: 전사 인사 데이터 관리, 조직 구조 편집, 공지사항 작성 등
 *
 * V111: hr → role_hr 변경 (레거시 제거)
 */
export const demoUserContext_hr: AuthContext = {
  userId: 'user_hr_001',
  roleAssignments: [
    {
      id: 'assign_hr_001',
      userId: 'user_hr_001',
      roleId: 'role_hr',
      isActive: true,
      createdAt: Timestamp.now(),
      createdBy: 'admin',
      notes: '인사관리자',
    },
  ],
  orgUnitId: 'org_team_hr',
  isLoaded: true,
};

/**
 * 승인자 (approver) AuthContext
 *
 * 강승인의 예시입니다.
 * - 역할: approver (승인자 - 마케팅팀)
 * - 소속: 마케팅팀 (org_team_marketing)
 * - 권한: 마케팅팀의 근무/연차 승인 처리
 *
 * **조직 범위 역할:**
 * 승인자는 특정 조직에 대해서만 권한을 가집니다.
 * 이 예시에서는 마케팅팀에 대해서만 승인 권한이 있습니다.
 */
export const demoUserContext_approver: AuthContext = {
  userId: 'user_approver_001',
  roleAssignments: [
    {
      id: 'assign_approver_002',
      userId: 'user_approver_001',
      roleId: 'approver',
      orgUnitId: 'org_team_marketing',
      isActive: true,
      createdAt: Timestamp.now(),
      createdBy: 'admin',
      notes: '마케팅팀 승인자',
    },
  ],
  orgUnitId: 'org_team_marketing',
  isLoaded: true,
};

/**
 * 임원 (executive) AuthContext
 *
 * 홍임원의 예시입니다.
 * - 역할: executive (임원)
 * - 소속: 경영지원본부 (org_dept_management)
 * - 권한: 전사 데이터 조회 (수정 불가)
 */
export const demoUserContext_executive: AuthContext = {
  userId: 'user_executive_001',
  roleAssignments: [
    {
      id: 'assign_executive_001',
      userId: 'user_executive_001',
      roleId: 'executive',
      isActive: true,
      createdAt: Timestamp.now(),
      createdBy: 'admin',
      notes: '임원',
    },
  ],
  orgUnitId: 'org_dept_management',
  isLoaded: true,
};

/**
 * 시스템 관리자 (system_admin) AuthContext
 *
 * 관리자 계정의 예시입니다.
 * - 역할: system_admin (시스템 관리자)
 * - 소속: 없음 (시스템 계정)
 * - 권한: 모든 권한 (제한 없음)
 *
 * **System Admin 특징:**
 * - 모든 권한 체크에서 true를 반환
 * - 조직 범위 제한 없음
 * - UI에서 직접 할당 불가 (보안상 이유)
 */
export const demoUserContext_superAdmin: AuthContext = {
  userId: 'user_system_admin',
  roleAssignments: [
    {
      id: 'assign_system_admin_001',
      userId: 'user_system_admin',
      roleId: 'system_admin',
      isActive: true,
      createdAt: Timestamp.now(),
      createdBy: 'system',
      notes: '시스템 관리자',
    },
  ],
  isLoaded: true,
};

/**
 * 비활성화된 역할이 포함된 AuthContext
 *
 * 역할 변경 이력을 보여주는 예시입니다.
 * - 현재 역할: org_manager (조직장 - 개발팀)
 * - 과거 역할: tl (비활성화됨)
 *
 * **역할 이력 관리:**
 * isActive: false인 역할 할당은 권한 체크에서 제외됩니다.
 * 하지만 데이터로는 남아있어 이력 추적이 가능합니다.
 */
export const demoUserContext_withInactiveRole: AuthContext = {
  userId: 'user_with_history_001',
  roleAssignments: [
    {
      id: 'assign_tl_002',
      userId: 'user_with_history_001',
      roleId: 'tl',
      isActive: false, // 비활성화됨
      createdAt: Timestamp.fromDate(new Date('2025-01-01')),
      createdBy: 'admin',
      updatedAt: Timestamp.fromDate(new Date('2025-12-05')),
      updatedBy: 'admin',
      deactivatedAt: Timestamp.fromDate(new Date('2025-12-05')),
      deactivatedBy: 'admin',
      notes: 'TL 역할 해제 (조직장 승진)',
    },
    {
      id: 'assign_org_manager_003',
      userId: 'user_with_history_001',
      roleId: 'org_manager',
      orgUnitId: 'org_team_dev',
      isActive: true, // 현재 활성
      createdAt: Timestamp.fromDate(new Date('2025-12-05')),
      createdBy: 'admin',
      notes: '개발팀 팀장 임명',
    },
  ],
  orgUnitId: 'org_team_dev',
  isLoaded: true,
};

/**
 * 모든 DEMO AuthContext를 하나의 객체로 묶음
 *
 * 테스트나 개발 시 쉽게 접근할 수 있도록
 * 모든 예시를 하나의 객체에 모았습니다.
 *
 * @example
 * // 콘솔에서 테스트
 * import { DEMO_AUTH_CONTEXTS } from './utils/demoAuthContexts';
 * import { hasPermission } from './utils/rbac';
 *
 * // 일반 직원의 공지사항 작성 권한 확인
 * hasPermission(DEMO_AUTH_CONTEXTS.general, 'notice.write') // false
 *
 * // HR의 공지사항 작성 권한 확인
 * hasPermission(DEMO_AUTH_CONTEXTS.hr, 'notice.write') // true
 */
export const DEMO_AUTH_CONTEXTS = {
  general: demoUserContext_general,
  tl: demoUserContext_tl,
  orgManager: demoUserContext_orgManager,
  orgManagerAndApprover: demoUserContext_orgManagerAndApprover,
  hr: demoUserContext_hr,
  approver: demoUserContext_approver,
  executive: demoUserContext_executive,
  superAdmin: demoUserContext_superAdmin,
  withInactiveRole: demoUserContext_withInactiveRole,
};

/**
 * DEMO 모드 테스트 가이드
 *
 * **1. 브라우저 콘솔에서 테스트:**
 *
 * ```javascript
 * // 모듈 import (개발 서버에서)
 * import { DEMO_AUTH_CONTEXTS } from './utils/demoAuthContexts';
 * import { hasPermission, canAccessRoute, getUserPermissions } from './utils/rbac';
 *
 * // 일반 직원 권한 테스트
 * const general = DEMO_AUTH_CONTEXTS.general;
 * console.log('일반 직원의 권한:', getUserPermissions(general));
 * console.log('공지사항 작성 가능?', hasPermission(general, 'notice.write')); // false
 * console.log('본인 근로 정보 수정 가능?', hasPermission(general, 'work.my.write')); // true
 *
 * // HR 권한 테스트
 * const hr = DEMO_AUTH_CONTEXTS.hr;
 * console.log('HR의 권한:', getUserPermissions(hr));
 * console.log('공지사항 작성 가능?', hasPermission(hr, 'notice.write')); // true
 * console.log('인사관리 페이지 접근?', canAccessRoute(hr, '/hr/employees')); // true
 *
 * // 조직장 권한 테스트
 * const orgManager = DEMO_AUTH_CONTEXTS.orgManager;
 * console.log('조직장의 권한:', getUserPermissions(orgManager, { orgUnitId: 'org_team_dev' }));
 * console.log('팀 관리 가능?', hasPermission(orgManager, 'team.my.manage', { orgUnitId: 'org_team_dev' })); // true
 * console.log('팀관리 페이지 접근?', canAccessRoute(orgManager, '/team/management')); // true
 * ```
 *
 * **2. React 컴포넌트에서 테스트:**
 *
 * ```tsx
 * import { DEMO_AUTH_CONTEXTS } from './utils/demoAuthContexts';
 * import { hasPermission } from './utils/rbac';
 *
 * function TestComponent() {
 *   const ctx = DEMO_AUTH_CONTEXTS.hr;
 *
 *   return (
 *     <div>
 *       {hasPermission(ctx, 'notice.write') && (
 *         <button>공지사항 작성</button>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 *
 * **3. 테스트 시나리오:**
 *
 * - 일반 직원: 본인 정보만 조회/수정, 공지사항/조직도 조회만 가능
 * - TL: 일반 직원 + MM 등록 가능
 * - 조직장: 팀원 정보 조회, 승인 처리, 팀 관리 가능
 * - HR: 모든 인사 데이터 관리, 공지사항 작성, 조직도 수정 가능
 * - 임원: 전사 데이터 조회 가능 (수정 불가)
 * - Super Admin: 모든 권한
 */
